/**
 * GET /configs only returns everything when neither `page` nor `pageSize` is sent, which is an
 * implicit backend behavior rather than a contract. Consumers that genuinely need every
 * publisher account config (e.g. deriving the countries a campaign can target) page through the
 * endpoint explicitly instead, with a hard page cap so a misbehaving backend cannot loop forever.
 */
import type {
  CampaignConfigurationControllerFindManyApiArg,
  CampaignConfigurationDto,
  Publisher,
} from 'src/store/generated/leela-api-generated';
import { Logger } from 'src/utils/logger';

export const ALL_CONFIGS_PAGE_SIZE = 100;
export const MAX_CONFIG_PAGES = 50;

const PUBLISHER_ACCOUNT_CONFIG_TYPE: CampaignConfigurationControllerFindManyApiArg['configType'] = 'PUBLISHER_ACCOUNT';

export interface ConfigsPageRequest {
  readonly url: string;
  readonly params: CampaignConfigurationControllerFindManyApiArg;
}

export interface PublisherAccountConfigsArg {
  readonly publisher: Publisher;
  readonly businessUnitIds?: readonly string[];
}

type ConfigsQueryResult =
  | { data: unknown; error?: undefined }
  | { error: unknown; data?: undefined };

export type ConfigsBaseQuery = (request: ConfigsPageRequest) => ConfigsQueryResult | PromiseLike<ConfigsQueryResult>;

type AllConfigsResult =
  | { data: CampaignConfigurationDto[] }
  | { error: unknown };

interface ConfigsPage {
  readonly data?: CampaignConfigurationDto[];
  readonly meta?: { pagination?: { pageCount?: number } };
}

/**
 * Narrows a GET /configs request to the given business units through the raw `filters`
 * pass-through; returns undefined when there is nothing to narrow by.
 */
export const buildBusinessUnitFilters = (
  businessUnitIds: readonly string[] | undefined,
): CampaignConfigurationControllerFindManyApiArg['filters'] =>
  businessUnitIds?.length ? { businessUnitId: { $in: [...businessUnitIds] } } : undefined;

/**
 * Sorting is deliberately never sent: sorting by campaignCount takes an in-memory backend path
 * capped at pageSize, and campaign counts are skipped because they cost an extra aggregation.
 */
export const buildConfigsPageRequest = (
  { publisher, businessUnitIds }: PublisherAccountConfigsArg,
  page: number,
): ConfigsPageRequest => {
  const filters = buildBusinessUnitFilters(businessUnitIds);
  return {
    url: '/configs',
    params: {
      configType: PUBLISHER_ACCOUNT_CONFIG_TYPE,
      publisher,
      includeCampaignCount: false,
      page,
      pageSize: ALL_CONFIGS_PAGE_SIZE,
      ...(filters && { filters }),
    },
  };
};

const isLastPage = (page: number, pageLength: number, pageCount: number | undefined): boolean =>
  pageCount === undefined || page >= pageCount || pageLength < ALL_CONFIGS_PAGE_SIZE;

export const fetchAllPublisherAccountConfigs = async (
  arg: PublisherAccountConfigsArg,
  baseQuery: ConfigsBaseQuery,
): Promise<AllConfigsResult> => {
  const configs: CampaignConfigurationDto[] = [];

  for (let page = 1; page <= MAX_CONFIG_PAGES; page++) {
    const result = await baseQuery(buildConfigsPageRequest(arg, page));
    if (result.error !== undefined) {
      return { error: result.error };
    }

    const { data: pageConfigs = [], meta } = (result.data ?? {}) as ConfigsPage;
    configs.push(...pageConfigs);
    if (isLastPage(page, pageConfigs.length, meta?.pagination?.pageCount)) {
      return { data: configs };
    }
  }

  void Logger.warn('Stopped paging publisher account configs at the page cap', {
    publisher: arg.publisher,
    maxPages: MAX_CONFIG_PAGES,
    collected: configs.length,
  });
  return { data: configs };
};
